'use strict';

const fetchRequest = async (url, method, body = null) => {
    try {
        const options = {
            method,
            headers: {
                'Content-Type': 'application/json',
            },
        };

        if (body) options.body = JSON.stringify(body);

        const response = await fetch(url, options);

        if (!response.ok) {
            const errorMessage = `Error: ${response.status} - ${response.statusText}`;
            throw new Error(errorMessage);
        }

        return await response.json();
    } catch (error) {
        console.error(`Request failed for ${method} ${url}:`, error);
        throw error;
    }
};

/**
 * Fetch the current state of the cart
 * @returns {Promise<Object>} Resolves with the current cart state
 */
const getCart = () => fetchRequest('/cart.js', 'GET');

/**
 * Clear all items from the cart
 * @returns {Promise<Object>} Resolves with the current cart state
 */
const clearCart = () => fetchRequest('/cart/clear.js', 'POST');

/**
 * Add an item to the cart
 * @param {Object} item - The item to add (must include `id` and `quantity`)
 * @returns {Promise<Object>} Resolves with the updated cart state
 * @throws {Error} If the input is invalid
 */
const addToCart = (item) => {
    if (!item || typeof item.id === 'undefined' || typeof item.quantity !== 'number') {
        throw new Error('Invalid item object. Must include `id` and `quantity`.');
    }
    return fetchRequest('/cart/add.js', 'POST', item);
};

/**
 * Update or remove an item in the cart
 * @param {Object} change - The change object (must include `id` and `quantity`)
 * @returns {Promise<Object>} Resolves with the updated cart state
 * @throws {Error} If the input is invalid
 */
const updateCart = (change) => {
    if (!change || typeof change.id === 'undefined' || typeof change.quantity !== 'number') {
        throw new Error('Invalid change object. Must include `id` and `quantity`.');
    }
    return fetchRequest('/cart/change.js', 'POST', change);
};

/**
 * Remove an item from the cart
 * @param {string} id - The ID of the item to remove
 * @returns {Promise<Object>} Resolves with the updated cart state
 */
const removeFromCart = (id) => {
    if (!id) {
        throw new Error('Invalid ID. Must provide an `id` to remove an item.');
    }
    return updateCart({ id, quantity: 0 });
};

export { getCart, clearCart, addToCart, updateCart, removeFromCart };